import React, { useState } from 'react'
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from 'react-leaflet'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

const DistributionMaps: React.FC = () => {
  const [activeMap, setActiveMap] = useState<'heat' | 'flow'>('heat')
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7))

  const { data: heatMapData, isLoading: heatMapLoading } = useQuery({
    queryKey: ['heatMap', selectedMonth],
    queryFn: () => api.getHeatMapData(selectedMonth).then(res => res.data),
    enabled: activeMap === 'heat'
  })

  const { data: flowData, isLoading: flowLoading } = useQuery({
    queryKey: ['distributionFlow', selectedMonth],
    queryFn: () => api.getDistributionFlowData(selectedMonth).then(res => res.data),
    enabled: activeMap === 'flow'
  })

  const { data: distributorLocations } = useQuery({
    queryKey: ['distributorLocations'],
    queryFn: () => api.getDistributorLocations().then(res => res.data)
  })

  const getFeatureStyle = (feature: any) => {
    if (activeMap === 'heat') {
      return {
        fillColor: feature.properties.fill_color || '#FEF0D9',
        weight: feature.properties.stroke_width || 1,
        opacity: 1,
        color: feature.properties.stroke_color || '#333333',
        fillOpacity: feature.properties.fill_opacity || 0.7
      }
    } else {
      return {
        color: feature.properties.stroke_color || '#FF6B35',
        weight: feature.properties.stroke_width || 2,
        opacity: feature.properties.stroke_opacity || 0.8
      }
    }
  }

  const onEachFeature = (feature: any, layer: any) => {
    if (feature.properties) {
      const props = feature.properties
      let popupContent = ''
      
      if (activeMap === 'heat') {
        popupContent = `
          <div class="p-2">
            <h4 class="font-semibold">${props.state_name}</h4>
            <p>Total Orders: ${props.total_orders}</p>
            <p>Total Value: $${props.total_value?.toLocaleString()}</p>
            <p>Total Quantity: ${props.total_quantity?.toLocaleString()}</p>
          </div>
        `
      } else {
        popupContent = `
          <div class="p-2">
            <h4 class="font-semibold">Distribution Flow</h4>
            <p>From: ${props.distributor_name}</p>
            <p>To: ${props.to_state}</p>
            <p>Product: ${props.product_sku}</p>
            <p>Quantity: ${props.quantity}</p>
            <p>Value: $${props.value?.toLocaleString()}</p>
          </div>
        `
      }
      
      layer.bindPopup(popupContent)
    }
  }

  const isLoading = (activeMap === 'heat' && heatMapLoading) || (activeMap === 'flow' && flowLoading)

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveMap('heat')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                activeMap === 'heat'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Heat Map
            </button>
            <button
              onClick={() => setActiveMap('flow')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                activeMap === 'flow'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Distribution Flow
            </button>
          </div>
          
          <div className="flex items-center space-x-4">
            <label htmlFor="month-select" className="text-sm font-medium text-gray-700">
              Month:
            </label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const date = new Date()
                date.setMonth(date.getMonth() - i)
                const value = date.toISOString().slice(0, 7)
                const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="h-96 md:h-[600px] relative">
          {isLoading && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          )}
          
          <MapContainer
            center={[39.8283, -98.5795]} // Center of US
            zoom={4}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Heat Map Data */}
            {activeMap === 'heat' && heatMapData?.features && (
              <GeoJSON
                key={`heat-${selectedMonth}`}
                data={heatMapData}
                style={getFeatureStyle}
                onEachFeature={onEachFeature}
              />
            )}
            
            {/* Flow Data */}
            {activeMap === 'flow' && flowData?.features && (
              <GeoJSON
                key={`flow-${selectedMonth}`}
                data={flowData}
                style={getFeatureStyle}
                onEachFeature={onEachFeature}
              />
            )}
            
            {/* Distributor Locations */}
            {distributorLocations?.features?.map((feature: any, index: number) => (
              <Marker
                key={index}
                position={[
                  feature.geometry.coordinates[1],
                  feature.geometry.coordinates[0]
                ]}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold">{feature.properties.name}</h4>
                    <p>{feature.properties.city}, {feature.properties.state}</p>
                    <p className="text-sm text-gray-600">{feature.properties.code}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Legend</h3>
        {activeMap === 'heat' ? (
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-200 rounded"></div>
              <span className="text-sm text-gray-600">Low Volume</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-400 rounded"></div>
              <span className="text-sm text-gray-600">Medium Volume</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-600 rounded"></div>
              <span className="text-sm text-gray-600">High Volume</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-1 bg-orange-500 rounded"></div>
              <span className="text-sm text-gray-600">Distribution Flow</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <span className="text-sm text-gray-600">Distributor Location</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DistributionMaps
